import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import saveAs from 'file-saver'; // 需要安装 file-saver: npm i file-saver
import * as XLSX from 'xlsx';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enNZ } from 'date-fns/locale/en-NZ';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Divider,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  TextField as MuiTextField,
  Checkbox,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
} from '@mui/material';
import { Bold } from 'lucide-react';

// ===== Types =====
export type SalesOrder = {
  id: number | string;

  productId?: string;
  product: { name: string };
  productName?: string;
  quantity?: number;
  unit?: string;
  orderDate: string;
};

export type SalesOrderLine = {
  id: number | string;
  orderId: number | string;
  materialId: number | string;
  materialName: string;
  quantity: number;
  dueDate: string;
};

export type InventoryItem = {
  id: number | string;
  productId?: string;
  product: { name: string, unit: string };
  quantity: number;
  onOrder?: number;
};

export type DemandByMaterial = {
  materialId: string | number;
  materialName: string;
  monthly: Record<string, number>;
  total: number;
};

export type MonthPlan = {
  materialId: string | number;
  materialName: string;
  stockQty: number;
  month: string;
  toBuy: number;
  requirement: number;
};

export type PlanPayload = Record<string, MonthPlan[]>;


// ===== Utilities =====
const ym = (isoDate: string) => new Date(isoDate).toISOString().slice(0, 7);
function sumRecord(rec: Record<string, number>): number {
  return Object.values(rec).reduce((a, b) => a + b, 0);
}
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';

function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}
function buildDemand(lines: SalesOrderLine[]): DemandByMaterial[] {
  const map = new Map<string | number, DemandByMaterial>();
  for (const l of lines) {
    const key = l.materialId;
    const month = ym(l.dueDate);
    const entry = map.get(key) ?? {
      materialId: l.materialId,
      materialName: l.materialName,
      monthly: {},
      total: 0,
    };
    entry.monthly[month] = (entry.monthly[month] ?? 0) + (l.quantity || 0);
    entry.total = sumRecord(entry.monthly);
    map.set(key, entry);
  }
  return Array.from(map.values()).sort((a, b) => String(a.materialName).localeCompare(String(b.materialName)));
}


// ===== Mock Data =====
const MOCK_SALES_ORDERS: SalesOrder[] = [
  { id: 1, product: { name: 'Customer A' }, orderDate: '2025-09-01' },
  { id: 2, product: { name: 'Customer B' }, orderDate: '2025-09-03' },
  { id: 3, product: { name: 'Customer C' }, orderDate: '2025-09-05' },
];

const MOCK_ORDER_LINES: SalesOrderLine[] = [
  { id: 1, orderId: 1, materialId: 101, materialName: 'Material X', quantity: 50, dueDate: '2025-09-10' },
  { id: 2, orderId: 1, materialId: 102, materialName: 'Material Y', quantity: 20, dueDate: '2025-09-12' },
  { id: 3, orderId: 2, materialId: 101, materialName: 'Material X', quantity: 30, dueDate: '2025-10-05' },
];

const MOCK_INVENTORY: InventoryItem[] = [
  { id: 101, product: { name: 'Material X', unit: '' }, quantity: 40, onOrder: 10 },
  { id: 102, product: { name: 'Material Y', unit: '' }, quantity: 15, onOrder: 0 },
];

// ===== Step 1: Pick Sales Orders =====

function SalesOrdersStep({
  onNext,
  selectedOrderIds,
}: {
  onNext: (selectedIds: (string | number)[], filteredProductIds: string[]) => void; // 修改回调函数签名
  selectedOrderIds: (string | number)[];
}) {
  // 只传递筛选且选中的订单到下一步
  const handleNext = () => {
    const filteredSelectedIds = selectedIds.filter(id =>
      sortedSalesOrders.map(o => o.id).includes(id)
    );

    // 获取筛选后的产品ID列表
    const filteredProductIds = Array.from(
      new Set(
        sortedSalesOrders
          .filter(o => selectedIds.includes(o.id))
          .map(o => o.productId)
          .filter(Boolean)
      )
    ) as string[];

    onNext(filteredSelectedIds, filteredProductIds);
  };
  // 原有状态
  const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<'productId' | 'orderDate' | null>('orderDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>(selectedOrderIds.length > 0 ? selectedOrderIds : []);

  // 新增：筛选状态
  const [filterProducts, setFilterProducts] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<any>(null);

  const [endDate, setEndDate] = useState<any>(null);
  const [availableProducts, setAvailableProducts] = useState<{ id: string; name: string }[]>([]);

  // 排序处理函数
  const handleSort = (field: 'productId' | 'orderDate') => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // 格式化日期为 YYYY-MM
  const formatToYearMonth = (dateStr: string): string => {
    return new Date(dateStr).toISOString().slice(0, 7);
  };


  // 筛选后的销售订单数据
  const filteredSalesOrders = useMemo(() => {
    return salesOrders.filter(order => {
      // 产品筛选
      if (filterProducts.length > 0 && !filterProducts.includes(order.productId || '')) {
        return false;
      }

      // 日期筛选 - 修改这里的逻辑
      if (startDate || endDate) {
        const orderMonth = formatToYearMonth(order.orderDate); // "2025-01"

        if (startDate) {
          const startMonth = startDate.toISOString().slice(0, 7); // 将 Date 对象转换为 "YYYY-MM"
          if (orderMonth < startMonth) {
            return false;
          }
        }

        if (endDate) {
          const endMonth = endDate.toISOString().slice(0, 7); // 将 Date 对象转换为 "YYYY-MM"
          if (orderMonth > endMonth) {
            return false;
          }
        }
      }

      return true;
    });
  }, [salesOrders, filterProducts, startDate, endDate]);

  // 排序后的销售订单数据
  const sortedSalesOrders = useMemo(() => {
    if (!sortBy) return filteredSalesOrders;

    return [...filteredSalesOrders].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortBy === 'productId') {
        aValue = a.productId || '';
        bValue = b.productId || '';
      } else { // orderDate
        aValue = a.orderDate;
        bValue = b.orderDate;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredSalesOrders, sortBy, sortDirection]);



  // 获取销售订单数据
  React.useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${API_URL}/sales/list`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (res.status === 401) {
          localStorage.removeItem('token');
          window.location.replace('/#/login');
          return null;
        }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        return res.json();
      })
      .then((data: any) => {
        if (!data) return;
        const sales = data as SalesOrder[];
        setSalesOrders(sales);
        setSelectedIds(selectedOrderIds.length > 0 ? selectedOrderIds : sales.map((o: SalesOrder) => o.id));

        // 提取可用的产品列表
        const products = Array.from(
          new Map(
            sales
              .filter((o: SalesOrder) => o.productId)
              .map((o: SalesOrder) => [o.productId, { id: String(o.productId), name: o.product.name }])
          ).values()
        ) as { id: string; name: string }[];
        setAvailableProducts(products);
      })
      .catch(err => console.error('Failed to fetch sales orders:', err))
      .finally(() => setLoading(false));
  }, [selectedOrderIds, API_URL]);

  const toggle = (id: string | number) => {
    setSelectedIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  // 全选/取消全选（基于筛选后的数据）
  const allSelected = sortedSalesOrders.length > 0 && selectedIds.length === sortedSalesOrders.length &&
    sortedSalesOrders.every(o => selectedIds.includes(o.id));
  const handleSelectAll = () => {
    if (allSelected) {
      // 取消选中筛选结果中的所有项
      setSelectedIds(prev => prev.filter(id => !sortedSalesOrders.map(o => o.id).includes(id)));
    } else {
      // 选中筛选结果中的所有项
      const newIds = sortedSalesOrders.map(o => o.id);
      setSelectedIds(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  // 清除筛选
  const clearFilters = () => {
    setFilterProducts([]);
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <Box>
      <Typography mt={2} variant="h6" gutterBottom>Step 1 · Select Sales Orders</Typography>

      {/* 筛选条件区域 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          {/* <Typography variant="subtitle2" gutterBottom>Filter Conditions</Typography> */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
            {/* 产品多选 */}
            <Autocomplete
              multiple
              options={availableProducts}
              getOptionLabel={(option) => `${option.id} - ${option.name}`}
              value={availableProducts.filter(p => filterProducts.includes(p.id))}
              onChange={(_, newValue) => {
                setFilterProducts(newValue.map(v => v.id));
              }}
              sx={{ '& .MuiAutocomplete-inputRoot': { minHeight: 50, height: 50 }, width: '100%', }}

              renderInput={(params) => (
                <MuiTextField {...params} label="Select Products" placeholder="Choose products..." size="small" />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.id}
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                  />
                ))
              }
            />


          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap', mt: 2 }}>
            {/* 开始日期 */}
            {/* <TextField
              label="Start Date"
              type="month"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 140 }}
            /> */}

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
              <DatePicker
                label="Start Date"
                views={['year', 'month']}
                format="MM/yyyy"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                    inputProps: { placeholder: 'MM/YYYY' },
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
              <DatePicker
                label="End Date"
                views={['year', 'month']}
                format="MM/yyyy"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: { '& .MuiPickersInputBase-root': { height: 50 }, '& input': { padding: '0 12px' } },
                    inputProps: { placeholder: 'MM/YYYY' },
                  },
                }}
              />
            </LocalizationProvider>

            {/* 结束日期 */}
            {/* <TextField
              label="End Date"
              type="month"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: 140 }}
            /> */}

            {/* 清除按钮 */}
            <Button variant="outlined" onClick={clearFilters} size="small" sx={{ minWidth: 80 }}>
              Clear
            </Button>
          </Box>

          {/* 筛选结果统计 */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {sortedSalesOrders.length} of {salesOrders.length} orders
          </Typography>
        </CardContent>
      </Card>

      <Card className="mt-4">
        <CardContent>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedIds.length > 0 && !allSelected &&
                          sortedSalesOrders.some(o => selectedIds.includes(o.id))}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>No.</TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('productId')}
                    >
                      ProductId {sortBy === 'productId' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableCell>
                    <TableCell>ProductName</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('orderDate')}
                    >
                      Order Date {sortBy === 'orderDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedSalesOrders.map((o, idx) => (
                    <TableRow
                      key={o.id}
                      hover
                      selected={selectedIds.includes(o.id)}
                      onClick={() => toggle(o.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(o.id)}
                          onChange={() => toggle(o.id)}
                        />
                      </TableCell>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{o.productId}</TableCell>
                      <TableCell>{o.product.name}</TableCell>
                      <TableCell>{o.quantity}</TableCell>
                      <TableCell>{o.unit}</TableCell>
                      <TableCell>{formatToYearMonth(o.orderDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Divider className="my-4" />
          <Stack direction="row" mt={2} spacing={2} alignItems="center">
            <Chip label={`Selected: ${selectedIds.length}`} />
            <Chip label={`Filtered: ${sortedSalesOrders.length}/${salesOrders.length}`} variant="outlined" />
            <Button
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={handleNext}
            >
              Next: Review BOM
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// ===== Step 2: review BOM =====
function BomReviewStep({
  selectedOrderIds,
  filteredProductIds, // 新增：筛选后的产品ID列表
  onBack,
  onNext,
}: {
  selectedOrderIds: (string | number)[];
  filteredProductIds?: string[]; // 新增参数
  onBack: () => void;
  onNext: () => void;
}) {
  const [bomList, setBomList] = useState<any[]>([]);
  const [reviewed, setReviewed] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentBom, setCurrentBom] = useState<any | null>(null);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    // 修改请求体，传递筛选后的产品ID
    const requestBody = {
      orderIds: selectedOrderIds,
      ...(filteredProductIds && filteredProductIds.length > 0 && { productIds: filteredProductIds })
    };

    fetch(`${API_URL}/bom/listByOrders`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })
      .then(res => {
        if (res.status === 401) {
          localStorage.removeItem('token');
          window.location.replace('/#/login');
          return null;
        }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (!data) return;

        // 如果有筛选条件，进一步过滤BOM列表
        let filteredBomList = data;
        if (filteredProductIds && filteredProductIds.length > 0) {
          filteredBomList = data.filter((bom: any) =>
            filteredProductIds.includes(bom.productId)
          );
        }

        setBomList(filteredBomList);
        const initReviewed: Record<string, boolean> = {};
        filteredBomList.forEach((bom: any) => { initReviewed[bom.id] = false; });
        setReviewed(initReviewed);
      })
      .catch(err => console.error('Failed to fetch BOM list:', err))
      .finally(() => setLoading(false));
  }, [selectedOrderIds, filteredProductIds]);



  const handleReview = (bom: any) => {
    setCurrentBom(bom);
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    if (currentBom) {
      setReviewed(r => ({ ...r, [currentBom.id]: true }));
      setDialogOpen(false);
      setCurrentBom(null);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setCurrentBom(null);
  };

  const allReviewed = Object.values(reviewed).every(v => v);

  return (
    <Box>
      <Typography mt={2} variant="h6" gutterBottom>
        Step 2 · Review BOM
      </Typography>
      <Card className="mt-4">
        <CardContent>
          {/* 显示筛选信息 */}
          {/* {filteredProductIds && filteredProductIds.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Filtered Products: {filteredProductIds.join(', ')}
            </Typography>
          )} */}

          {/* 一键复核按钮 */}
          <Stack direction="row" spacing={2} mb={2}>
            <Button
              variant="contained"
              color="primary"
              disabled={Object.values(reviewed).every(v => v) || bomList.length === 0}
              onClick={() => {
                // 一键复核所有 BOM
                const allReviewedObj = Object.fromEntries(bomList.map(bom => [bom.id, true]));
                setReviewed(allReviewedObj);
              }}
            >
              Mark All as Reviewed
            </Button>
          </Stack>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>No.</TableCell>
                    <TableCell>Product ID</TableCell>
                    <TableCell>Product Name</TableCell>
                    {/* <TableCell>BOM Version</TableCell> */}
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bomList.map((bom, idx) => (
                    <TableRow key={bom.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{bom.productId}</TableCell>
                      <TableCell>{bom.productName}</TableCell>
                      {/* <TableCell>{bom.version}</TableCell> */}
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          disabled={reviewed[bom.id]}
                          onClick={() => handleReview(bom)}
                        >
                          {reviewed[bom.id] ? 'reviewed' : 'review'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Stack direction="row" mt={2} spacing={2}>
            <Button variant="outlined" onClick={onBack}>Back</Button>
            <Button
              variant="contained"
              disabled={!allReviewed}
              onClick={onNext}
            >
              Next: Load Inventory
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>BOM Detail</DialogTitle>
        <DialogContent>
          {currentBom && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>ProductId: {currentBom.productId}</Typography>

              <Typography variant="subtitle2" gutterBottom>ProductName: {currentBom.productName}</Typography>
              <Typography variant="subtitle2" gutterBottom>Version: {currentBom.version}</Typography>
              <Typography variant="subtitle2" gutterBottom>Blend Size: {currentBom.blendSize} (kg)</Typography>

              {/* 原料表格 */}
              <Typography variant="h6" mt={2} mb={1}>RawMaterial</Typography>
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>MaterialName</TableCell>
                    <TableCell align="right">Quantity (kg)</TableCell>
                    {/* <TableCell align="right">Proportion</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(currentBom.items ?? [])
                    .filter(item => item.type === 'RawMaterial')
                    .map((item, idx) => (
                      <TableRow key={item.id ?? idx}>
                        <TableCell>{item.productId} - {item.productName}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        {/* <TableCell align="right">{item.proportion}</TableCell> */}
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {
                        (currentBom.items ?? [])
                          .filter(item => item.type === 'RawMaterial')
                          .reduce((sum, item) => sum + (item.quantity ?? 0), 0)
                          .toFixed(2)
                      }
                    </TableCell>
                    {/* <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {
                        (currentBom.items ?? [])
                          .filter(item => item.type === 'RawMaterial')
                          .reduce((sum, item) => sum + (item.proportion ?? 0), 0)
                          .toFixed(2)
                      }
                    </TableCell> */}
                  </TableRow>
                </TableBody>
              </Table>

              {/* 包装材料表格 */}
              <Typography variant="h6" mt={2} mb={1}>PackagingMaterial</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>MaterialName</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(currentBom.items ?? [])
                    .filter(item => item.type === 'PackagingMaterial')
                    .map((item, idx) => (
                      <TableRow key={item.id ?? idx}>
                        <TableCell>{item.productId} - {item.productName}</TableCell>
                        <TableCell align="right">{item.quantity} ({item.unit})</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ===== Step 3: Load Inventory =====
function InventoryStep({
  onBack,
  onNext,
}: {
  onBack: () => void;
  onNext: (payload: { blendAdjust: number, lossRate: number }) => void;
}) {
  const [inv, setInv] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [blendAdjust, setBlendAdjust] = useState<number>(1);
  const [lossRate, setLossRate] = useState<number>(2);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${API_URL}/stock/list`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => {
        if (res.status === 401) {
          localStorage.removeItem('token');
          window.location.replace('/#/login');
          return null;
        }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        return res.json();
      })
      .then(data => { if (data) setInv(data); })
      .catch(err => console.error('Failed to fetch inventory:', err))
      .finally(() => setLoading(false));
  }, [API_URL]);


  return (
    <Box >
      <Typography mt={2} variant="h6" gutterBottom>Step 3 · Load Inventory</Typography>
      <Card className="mt-4">
        <CardContent>
          {/* 参数设置 */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <Typography>Blend Adjust:</Typography>
            <MuiTextField
              type="number"
              size="small"
              value={blendAdjust}
              defaultValue={1}
              onChange={e => {
                const val = Number(e.target.value);
                setBlendAdjust(val < 0 ? 0 : val);
              }}
              sx={{ width: 160, minWidth: 100 }}
            />
            <Typography>Loss Rate:</Typography>
            <MuiTextField
              type="number"
              size="small"
              value={lossRate}
              defaultValue={2}
              onChange={e => {
                const val = Number(e.target.value);
                setLossRate(val < 0 ? 0 : val);
              }}
              sx={{ width: 160, minWidth: 100 }}
            />
          </Stack>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>No.</TableCell>
                    <TableCell>Material ID</TableCell>
                    <TableCell>Material Name</TableCell>

                    <TableCell align="left">On Hand</TableCell>
                    <TableCell>Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inv.map((row, idx) => (
                    <TableRow key={`inv-${row.id}`}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{row.productId}</TableCell>
                      <TableCell>{row.product.name}</TableCell>

                      <TableCell align="left">{row.quantity ?? 0}</TableCell>
                      <TableCell>{row.product.unit}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack direction="row" mt={2} spacing={2} className="mt-4">
            <Button variant="outlined" onClick={onBack}>Back</Button>
            <Button
              variant="contained"
              disabled={inv.length === 0}
              onClick={() => onNext({ blendAdjust, lossRate })}
            >
              Confirm
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box >
  );
}

// ===== Step 4: Confirm & Calculate =====
function ConfirmStep({
  payload,
  onBack,
  onFinish,
}: {
  payload: { plan: Record<string, MonthPlan[]> };
  onBack: () => void;
  onFinish: (plan: MonthPlan[]) => void;
}) {
  // 解析成表格数据
  const { months, materials } = React.useMemo(() => {
    if (!payload?.plan) return { months: [], materials: [] };
    const monthKeys = Object.keys(payload.plan).sort();
    const matMap: Record<string, { materialId: string | number; materialName: string; stockQty: number; monthData: Record<string, number>; monthRequirementData: Record<string, number> }> = {};

    for (const month of monthKeys) {
      for (const item of payload.plan[month] || []) {
        const key = String(item.materialId);
        if (!matMap[key]) {
          matMap[key] = {
            materialId: item.materialId,
            materialName: item.materialName,
            stockQty: item.stockQty || 0,
            monthData: {},
            monthRequirementData: {}
          };
        }
        matMap[key].monthData[month] = item.toBuy ?? 0;
        matMap[key].monthRequirementData[month] = item.requirement ?? 0;
      }
    }

    return { months: monthKeys, materials: Object.values(matMap) };
  }, [payload?.plan]);


  const exportExcel = () => {
    if (!materials || materials.length === 0) return;

    // 第一行（组标题）和第二行（月份标题）
    const headerRow1 = [
      '',
      '',
      '',
      '',
      // requirement group - 合并这些单元格，后面用空字符串占位
      'requirement',
      ...Array(months.length - 1).fill(''),
      // remaining group - 合并这些单元格，后面用空字符串占位
      'Remaining',
      ...Array(months.length - 1).fill(''),
    ];

    const headerRow2 = [
      'No.', 'Material ID', 'Material Name', 'BODCO SOH',
      // requirement 各个月份只显示 ${m}
      ...months,
      // remaining 各个月份也只显示 ${m}
      ...months,
    ];

    // 数据行
    const rows = materials.map((mat, idx) => [
      idx + 1,
      mat.materialId,
      mat.materialName,
      mat.stockQty,
      ...months.map(m => mat.monthRequirementData[m] ?? 0),
      ...months.map(m => mat.monthData[m] ?? 0)
    ]);

    // 组装数据（两行头 + 数据）
    const sheetData = [headerRow1, headerRow2, ...rows];

    // 创建 worksheet
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // 合并单元格：合并 requirement 的列 和 Remaining 的列（第一行）
    const reqStartCol = 4;
    const reqEndCol = reqStartCol + months.length - 1;
    const remStartCol = reqEndCol + 1;
    const remEndCol = remStartCol + months.length - 1;

    ws['!merges'] = [
      { s: { r: 0, c: reqStartCol }, e: { r: 0, c: reqEndCol } }, // requirement 合并
      { s: { r: 0, c: remStartCol }, e: { r: 0, c: remEndCol } }, // Remaining 合并
    ];

    // 设置每列宽度（单位为字符数）
    ws['!cols'] = [
      { wch: 6 },   // No.
      { wch: 12 },  // Material ID
      { wch: 24 },  // Material Name
      { wch: 12 },  // BODCO SOH
      ...months.map(() => ({ wch: 14 })), // Requirement 月份
      ...months.map(() => ({ wch: 14 })), // Remaining 月份
    ];

    // 创建 workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Plan');

    // 导出为 blob 并下载
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'purchase_plan.xlsx');
  };
  // 导出功能
  const exportCsv = () => {
    if (!materials || materials.length === 0) return;

    // 第一行（组标题）和第二行（月份标题）
    const headerRow1 = [
      'No.',
      'Material ID',
      'Material Name',
      'BODCO SOH',
      'requirement',
      ...Array(months.length - 1).fill(''),
      'Remaining',
      ...Array(months.length - 1).fill(''),
    ];

    const headerRow2 = [
      '', '', '', '',
      ...months,
      ...months,
    ];

    // 数据行
    const rows = materials.map((mat, idx) => [
      idx + 1,
      mat.materialId,
      mat.materialName,
      mat.stockQty,
      ...months.map(m => mat.monthRequirementData[m] ?? 0),
      ...months.map(m => mat.monthData[m] ?? 0)
    ]);

    // 构造 CSV 内容（两行头 + 数据行）
    const allRows = [headerRow1, headerRow2, ...rows];
    const csvContent = allRows
      .map(r => r.map(v => `"${String(v ?? '')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'purchase_plan.csv');
  };



  return (
    <Box sx={{ width: "100%" }}>
      <Typography mt={2} variant="h6" gutterBottom>
        Step 4 · Material Procurement Plan
      </Typography>
      <Card className="mt-4">
        <CardContent>
          <TableContainer sx={{ maxHeight: 300, overflowY: 'auto', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    No.
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    Material ID
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    Material Name
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    BODCO SOH
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={months.length}
                    sx={{

                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    Requirement
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={months.length}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: '#fff',
                      border: '1px solid #ddd'
                    }}
                  >
                    Remaining
                  </TableCell>
                </TableRow>
                <TableRow>
                  {months.map(m => (
                    <TableCell
                      key={m + '-requirement'}
                      align="center"
                      sx={{
                        width: 100,
                        minWidth: 100,
                        position: 'sticky',
                        top: 38,
                        zIndex: 1,
                        background: '#fff',
                        border: '1px solid #ddd'
                      }}
                    >
                      {m}
                    </TableCell>
                  ))}
                  {months.map(m => (
                    <TableCell
                      key={m + '-remaining'}
                      align="center"
                      sx={{
                        width: 100,
                        minWidth: 100,
                        border: '1px solid #ddd',
                        position: 'sticky',
                        top: 38,
                        zIndex: 1,
                        background: '#fff'
                      }}
                    >
                      {m}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map((mat, idx) => (
                  <TableRow key={mat.materialId}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{mat.materialId}</TableCell>
                    <TableCell>{mat.materialName}</TableCell>
                    <TableCell>{mat.stockQty}</TableCell>
                    {months.map(m => (
                      <>
                        <TableCell key={m + '-blend'} align="right" sx={{ color: '#1976d2', fontWeight: 500 }} > {mat.monthRequirementData[m] ?? 0} </TableCell>


                      </>
                    ))}
                    {months.map(m => (
                      <>

                        <TableCell key={m} align="right" sx={
                          mat.monthData[m] < 0
                            ? { color: 'red', fontWeight: 700 }
                            : { color: 'green', fontWeight: 700 }
                        }
                        >
                          {mat.monthData[m] ?? 0}
                        </TableCell>
                      </>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* <Button variant="outlined" className="mt-4" onClick={exportCsv}>Export CSV</Button> */}
          <Stack direction="row" mt={2} spacing={2}>
            <Button variant="outlined" onClick={onBack}>Back</Button>
            <Button variant="outlined" onClick={() => exportExcel()}>Export Excel</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box >
  );
}

// ===== Main Wizard =====
export default function PurchasePlanWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOrderIds, setSelectedOrderIds] = useState<(string | number)[]>([]);
  const [payload, setPayload] = useState<null | { plan: Record<string, MonthPlan[]> }>(null);
  const [filteredProductIds, setFilteredProductIds] = useState<string[]>([]); // 新增状态

  return (
    <Box className="p-4">
      <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>Material Procurement Planning</Typography>

      <Stepper activeStep={activeStep} alternativeLabel className="mb-6">
        {['Select Orders', 'Review BOM', 'Load Inventory', 'Confirm & Calculate'].map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <SalesOrdersStep
          onNext={(ids, productIds) => {
            setSelectedOrderIds(ids);
            setFilteredProductIds(productIds);

            setActiveStep(1);
          }}
          selectedOrderIds={selectedOrderIds}
        />
      )}
      {activeStep === 1 && (
        <BomReviewStep
          selectedOrderIds={selectedOrderIds}
          filteredProductIds={filteredProductIds} // 传递筛选后的产品ID

          onBack={() => setActiveStep(0)}
          onNext={() => setActiveStep(2)}
        />
      )}

      {activeStep === 2 && (
        <InventoryStep
          onBack={() => setActiveStep(1)}

          onNext={async ({ blendAdjust, lossRate }) => {
            const token = localStorage.getItem('token');

            try {
              const res = await fetch(`${API_URL}/materials/calculate`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  orderIds: selectedOrderIds, blendAdjust,
                  lossRate,
                }),
              });

              if (res.status === 401) {
                localStorage.removeItem('token');
                window.location.replace('/#/login');
                return;
              }
              if (!res.ok) throw new Error(`Error ${res.status}`);

              const data: PlanPayload = await res.json();
              setPayload({ plan: data });
              setActiveStep(3);
            } catch (err) {
              console.error('Failed to calculate plan:', err);
            }
          }}

        />
      )}

      {activeStep === 3 && payload && (
        <ConfirmStep
          payload={payload}
          onBack={() => setActiveStep(2)}
          onFinish={(plan) => console.log('Computed plan lines:', plan)}
        />
      )}
    </Box>
  );
}
